import React, { useState } from "react";
import { WrapperSeamless } from "../../layout-components";
import Table from "../../components/tables/table";
import { Button } from "@material-ui/core";
import { useGetList } from "../../hooks";
import { Link } from "react-router-dom";
import * as Api from "../../service";
import useStore from "../../context";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { useTranslation } from "localization/translation";
import { Select, CopyButton } from "../../components";
import { dateFormatter } from "helpers/dateFormatter";

const ACTIVE_FILTER_OPTIONS = [
  { id: "true", name: "active" },
  { id: "false", name: "inactive" },
];

const COUNTRIES_QUERY = {
  sort: "name_en",
  order: "asc",
};

const formatDate = (value) =>
  value ? dateFormatter(value, "DD/MM/YYYY") : "—";

const formatDiscountValue = (item) => {
  if (item.type === "percentage") {
    return `${item.value}%`;
  }
  if (item.type === "fixed_price") {
    const prices = (item.prices || [])
      .map((p) => `${p.price} ${p.currency_code || ""}`.trim())
      .join(", ");
    return prices || "—";
  }
  if (item.type === "fixed_amount") {
    return `${item.value}`;
  }
  if (item.type === "extra_days") {
    return item.value != null ? String(item.value) : "—";
  }
  return "—";
};

const formatType = (type, translation) => {
  if (type === "percentage") return translation.discount_type_percentage;
  if (type === "fixed_price") return translation.discount_type_fixed_price;
  if (type === "fixed_amount") return translation.discount_type_fixed_amount;
  if (type === "extra_days") return translation.discount_type_extra_days;
  return type;
};

const formatSource = (item, translation) => {
  if (item.source === "auto" || item.is_system_generated) {
    return translation.promo_source_auto;
  }
  return translation.promo_source_admin;
};

const CountryStatusFilters = ({
  query,
  setQuery,
  countries,
  countryNameField,
  translation,
}) => (
  <div className="table-header-filters">
    <Select
      onChange={(val) =>
        setQuery((st) => {
          const next = { sort: st.sort, order: st.order };
          if (st.active) next.active = st.active;
          if (val) next.country_id = val;
          return next;
        })
      }
      value={query.country_id || ""}
      field={countryNameField}
      label={translation.country}
      options={countries || []}
    />
    <Select
      onChange={(val) =>
        setQuery((st) => {
          const next = { sort: st.sort, order: st.order };
          if (st.country_id) next.country_id = st.country_id;
          if (val) next.active = val;
          return next;
        })
      }
      value={query.active || ""}
      field="name"
      label={translation.status}
      options={ACTIVE_FILTER_OPTIONS}
      isFieldTranslate
    />
  </div>
);

export default function PromoCodesList({ variant = "promo" }) {
  const isAmbassador = variant === "ambassador";
  const codesApi = isAmbassador ? Api.ambassadorCodes : Api.promoCodes;
  const { setRemove, language, lang } = useStore();
  const translation = useTranslation();
  const [query, setQuery] = useState({
    sort: "created_at",
    order: "desc",
  });

  const [items, total, refresh, setItems] = useGetList(
    codesApi.getAll,
    query
  );
  const [countries] = useGetList(
    isAmbassador ? Api.countries.getAll : null,
    COUNTRIES_QUERY
  );

  const countryNameField = `name_${lang || language || "en"}`;

  const formatPlans = (item) => {
    const linked = item.plans || [];
    if (!linked.length) return "—";
    return linked
      .map((plan) => {
        const name =
          plan[`name_${language}`] || plan.name_en || `#${plan.plan_id}`;
        const periodKey =
          plan.billing_period === "both"
            ? "billing_period_both"
            : plan.billing_period;
        const period = translation[periodKey] || plan.billing_period;
        return `${name} (${period})`;
      })
      .join(", ");
  };

  const ambassadorRows = items?.map((item) => (
    <>
      <td className="text-left px-4">
        <div className="d-flex align-items-center">
          <div className="font-size-sm font-weight-bold">{item.code}</div>
          <CopyButton text={item.code} />
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{item.name}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {item.country?.[countryNameField] ||
            item.country?.name_en ||
            "—"}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {item.is_active ? translation.active : translation.inactive}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{item.users_count ?? 0}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {formatDiscountValue(item)}
        </div>
      </td>
      <td className="text-right actions_filed">
        <Link to={`/ambassador-codes/create/${item.id}`}>
          <Button className="btn-neutral-first mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
            <EditIcon color="primary" />
          </Button>
        </Link>
        <Button
          onClick={() =>
            setRemove(() => () => codesApi.delete(item.id).then(refresh))
          }
          className="btn-neutral-danger mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
        >
          <DeleteIcon color="error" />
        </Button>
      </td>
    </>
  ));

  const promoRows = items?.map((item) => (
    <>
      <td className="text-left px-4">
        <div className="d-flex align-items-center">
          <div className="font-size-sm font-weight-bold">{item.code}</div>
          <CopyButton text={item.code} />
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{item.name}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{formatType(item.type, translation)}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{formatDiscountValue(item)}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{formatSource(item, translation)}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">{formatPlans(item)}</div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {formatDate(item.start_date || item.valid_from)}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {formatDate(item.end_date || item.valid_until)}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {formatDate(item.usage_start_date || item.usage_starts_at)}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {formatDate(item.usage_end_date || item.usage_ends_at)}
        </div>
      </td>
      <td className="text-left px-4">
        <div className="font-size-sm">
          {item.is_active ? translation.active : translation.inactive}
        </div>
      </td>
      <td className="text-right actions_filed">
        <Link to={`/promo-codes/create/${item.id}`}>
          <Button className="btn-neutral-first mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center">
            <EditIcon color="primary" />
          </Button>
        </Link>
        <Button
          onClick={() =>
            setRemove(() => () => codesApi.delete(item.id).then(refresh))
          }
          className="btn-neutral-danger mx-1 shadow-none d-30 border-0 p-0 d-inline-flex align-items-center justify-content-center"
        >
          <DeleteIcon color="error" />
        </Button>
      </td>
    </>
  ));

  return (
    <WrapperSeamless>
      <Table
        fields={
          isAmbassador ? (
            <>
              <th className="text-left px-4">
                <span className="mr-3">{translation.code}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.name}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.country}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.is_active}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.users_count}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.discount_value}</span>
              </th>
            </>
          ) : (
            <>
              <th className="text-left px-4">
                <span className="mr-3">{translation.code}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.name}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.discount_type}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.discount_value}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.promo_source}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.plans}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.valid_from}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.valid_until}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.usage_starts_at}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.usage_ends_at}</span>
              </th>
              <th className="text-left px-4">
                <span className="mr-3">{translation.is_active}</span>
              </th>
            </>
          )
        }
        header={
          isAmbassador ? (
            <CountryStatusFilters
              query={query}
              setQuery={setQuery}
              countries={countries}
              countryNameField={countryNameField}
              translation={translation}
            />
          ) : (
            <Select
              onChange={(val) =>
                setQuery((st) => {
                  const next = { sort: st.sort, order: st.order };
                  if (val) next.active = val;
                  return next;
                })
              }
              value={query.active || ""}
              field="name"
              label={translation.status}
              options={ACTIVE_FILTER_OPTIONS}
              className="ml-4 mr-auto w-25"
              isFieldTranslate
            />
          )
        }
        noDrag
        title={
          isAmbassador
            ? translation.ambassador_codes
            : translation.promo_codes
        }
        items={items}
        setItems={setItems}
        total={total}
        rows={isAmbassador ? ambassadorRows : promoRows}
        createLink={
          isAmbassador ? "ambassador-codes/create" : "promo-codes/create"
        }
      />
    </WrapperSeamless>
  );
}
